/**
 * Board Layout Composable
 *
 * Manages DDC board layout state and API operations for the board layout editor.
 * Handles controller placement, accessory management, and spare space calculations.
 */

import { ref, computed } from 'vue';
import { api } from '@/services/api';

/**
 * Create a board layout management composable
 * @param {number} siteId - Site ID
 * @param {number} equipmentId - Equipment ID (DDC board)
 * @returns {Object} Board layout state and operations
 */
export function useBoardLayout(siteId, equipmentId) {
  // State
  const layout = ref(null);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref(null);
  const accessories = ref([]);
  const availableControllers = ref([]);

  /**
   * Load board layout from API
   */
  const loadLayout = async () => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(`/sites/${siteId}/equipment/${equipmentId}/board-layout`);
      layout.value = response;
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load board layout';
      console.error('[Board Layout] Load error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Save complete board layout
   * @param {Object} layoutData - Complete layout data with board and items
   */
  const saveLayout = async (layoutData) => {
    saving.value = true;
    error.value = null;

    try {
      const response = await api.put(`/sites/${siteId}/equipment/${equipmentId}/board-layout`, layoutData);
      layout.value = response;
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to save board layout';
      console.error('[Board Layout] Save error:', err);
      throw err;
    } finally {
      saving.value = false;
    }
  };

  /**
   * Add a single item to the board
   * @param {Object} itemData - Item data (controller or accessory)
   */
  const addItem = async (itemData) => {
    try {
      const response = await api.post(`/sites/${siteId}/equipment/${equipmentId}/board-layout/items`, itemData);

      // Update local layout
      if (layout.value && layout.value.items) {
        layout.value.items.push(response);
      }

      return response;
    } catch (err) {
      error.value = err.message || 'Failed to add item';
      console.error('[Board Layout] Add item error:', err);
      throw err;
    }
  };

  /**
   * Update an item's position or properties
   * @param {string} itemId - Item UUID
   * @param {Object} itemData - Updated item data
   */
  const updateItem = async (itemId, itemData) => {
    try {
      const response = await api.put(
        `/sites/${siteId}/equipment/${equipmentId}/board-layout/items/${itemId}`,
        itemData,
      );

      // Update local layout
      if (layout.value && layout.value.items) {
        const index = layout.value.items.findIndex((item) => item.id === itemId);
        if (index > -1) {
          layout.value.items[index] = { ...layout.value.items[index], ...response };
        }
      }

      return response;
    } catch (err) {
      error.value = err.message || 'Failed to update item';
      console.error('[Board Layout] Update item error:', err);
      throw err;
    }
  };

  /**
   * Remove an item from the board
   * @param {string} itemId - Item UUID
   */
  const removeItem = async (itemId) => {
    try {
      await api.delete(`/sites/${siteId}/equipment/${equipmentId}/board-layout/items/${itemId}`);

      // Update local layout
      if (layout.value && layout.value.items) {
        layout.value.items = layout.value.items.filter((item) => item.id !== itemId);
      }
    } catch (err) {
      error.value = err.message || 'Failed to remove item';
      console.error('[Board Layout] Remove item error:', err);
      throw err;
    }
  };

  /**
   * Load accessories library from API
   */
  const loadAccessories = async () => {
    try {
      const response = await api.get('/board-layout/accessories');
      accessories.value = response;
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load accessories';
      console.error('[Board Layout] Load accessories error:', err);
      throw err;
    }
  };

  /**
   * Load available controllers (not yet placed on board)
   */
  const loadAvailableControllers = async () => {
    try {
      const response = await api.get(`/sites/${siteId}/equipment/${equipmentId}/board-layout/available-controllers`);
      availableControllers.value = response;
      return response;
    } catch (err) {
      error.value = err.message || 'Failed to load available controllers';
      console.error('[Board Layout] Load controllers error:', err);
      throw err;
    }
  };

  // Computed
  const hasLayout = computed(() => layout.value !== null);
  const itemCount = computed(() => layout.value?.items?.length || 0);
  const boardDimensions = computed(() => layout.value?.board || { width_mm: 800, height_mm: 600 });
  const spareSpace = computed(() => layout.value?.spare_space || 0);

  return {
    // State
    layout,
    loading,
    saving,
    error,
    accessories,
    availableControllers,

    // Computed
    hasLayout,
    itemCount,
    boardDimensions,
    spareSpace,

    // Actions
    loadLayout,
    saveLayout,
    addItem,
    updateItem,
    removeItem,
    loadAccessories,
    loadAvailableControllers,
  };
}
